import React, { ComponentProps, ReactNode, useEffect, useState } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';

import { Button } from '@/components/core/button';
import { DialogContent, DialogFooter } from '@/components/core/dialog';
import { Form } from '@/components/core/form';
import { DialogWithContext, useDialogContext } from '@/components/dialog-with-context';
import { cn } from '@/shared/helpers';
import { Result } from '@/shared/helpers/Result';

export const FormDialog = DialogWithContext;

type FormDialogContentProps<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
  TTransformedValues = TFieldValues,
> = Omit<ComponentProps<typeof DialogContent>, 'onSubmit'> & {
  /** Provide the return value of useForm here. */
  form: UseFormReturn<TFieldValues, TContext, TTransformedValues>;

  /**
   * Called upon successful form submission with the validated form data. If the form's operation is asynchronous,
   * return a `Promise` here and the dialog will stay open in a pending state until the promise resolves.
   *
   * If you return a `Result` from the function, the dialog will not close if it is a failure result, preventing users
   * from having to refill the form if the mutation fails.
   */
  onSubmit: (
    data: TTransformedValues,
    event?: React.BaseSyntheticEvent,
  ) => void | Promise<Result> | Promise<void>;

  /** An optional label override for the dialog's cancel button. Defaults to "Cancel". */
  cancelLabel?: ReactNode;

  /** An optional label override for the dialog's submit button. Defaults to "Save". */
  submitLabel?: ReactNode;

  /** Whether the submit button should be disabled. */
  isSubmitDisabled?: boolean;
};

export function FormDialogContent<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
  TTransformedValues = TFieldValues,
>({
  form,
  onSubmit,
  cancelLabel = 'Cancel',
  submitLabel = 'Save',
  isSubmitDisabled,
  className,
  children,
  ...props
}: FormDialogContentProps<TFieldValues, TContext, TTransformedValues>) {
  const { isOpen, setIsOpen } = useDialogContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      form.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to reset when opened
  }, [isOpen]);

  return (
    <DialogContent className={cn('sm:max-w-lg', className)} {...props}>
      <Form {...form}>
        <form
          className="contents"
          onSubmit={form.handleSubmit(async (data, event) => {
            setIsSubmitting(true);
            try {
              const result = await onSubmit(data, event);
              if (!result || result.isSuccess) setIsOpen(false);
            } finally {
              setIsSubmitting(false);
            }
          })}
        >
          {children}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
              {cancelLabel}
            </Button>
            <Button type="submit" loading={isSubmitting} disabled={isSubmitDisabled}>
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </DialogContent>
  );
}
